import { computed } from "vue";
import { useSubscriptionsStore } from "@shared/stores/subscriptions.ts";
import { entitlementsService } from "@shared/services/entitlements.ts";
import { EntitlementName } from "@shared/types/localTypes/entitlements.ts";
import { isAiAddendumApplicable as isAiAddendumStateApplicable } from "@shared/utils/ai-addendum";

export const isEntitlementEnabled = (value: string | undefined): boolean => {
  if (!value) return false;
  if (value === "true") return true;
  if (value === "false") return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
};

export default () => {
  const subscriptionsStore = useSubscriptionsStore();
  const subscriptionMeta = computed(
    () => subscriptionsStore.currentSubscription?.meta,
  );

  const subscriptionPaymentType = computed(
    () => subscriptionsStore.currentSubscription?.paymentType,
  );

  const requestDowngradeSubscriptionMeta = computed(() => {
    //Available to all
    return false;
  });

  const removeSecurityPackMeta = computed(() => {
    //Available to EnterpriseX Monthly
    // return subscriptionPaymentType.value === "ENTERPRISE_X_MONTHLY";
    // currently not available
    return false;
  });

  const cancelSubscriptionMeta = computed(() => {
    //Not Available to MP
    if (subscriptionMeta.value?.isMP) {
      return false;
    }

    return (
      //Available to Monthly - Pro Team, Enterprise and EnterpriseX
      subscriptionPaymentType.value === "PRO_TEAM_MONTHLY" ||
      subscriptionPaymentType.value === "ENTERPRISE_MONTHLY" ||
      subscriptionPaymentType.value === "ENTERPRISE_X_MONTHLY" ||
      //Available to Free
      subscriptionMeta.value?.isFree ||
      //Available to Internal - Internal and Internal ProX
      subscriptionMeta.value?.isInternal ||
      //Available to JCR
      subscriptionMeta.value?.isJCR ||
      false
    );
  });

  const artifactoryConfigurationTransferMeta = computed(() => {
    //Available to all subscriptions
    return true;
  });

  const publicRootCertificateMeta = computed(() => {
    //Available Only for Enterprise Plus - all payment types
    return subscriptionMeta.value?.isEnterprisePlus;
  });

  const cloudChangesMeta = computed(() => {
    return (
      subscriptionMeta.value?.hasServers &&
      !!(
        subscriptionMeta.value?.isEnterprise ||
        subscriptionMeta.value?.isEnterpriseTeam ||
        //Available to ProTeam - Monthly and Annual
        subscriptionMeta.value?.isProTeam ||
        //Available to EnterpriseX - Monthly and Annual
        subscriptionMeta.value?.isEnterpriseX ||
        //Available to JCR
        subscriptionMeta.value?.isJCR
      )
    );
  });

  const isEntitledCname = computed(() => {
    const currentSubscription = subscriptionsStore.currentSubscription;
    if (!currentSubscription) {
      return false;
    }
    const cnameEntitlement = entitlementsService.getEntitlement(
      currentSubscription,
      EntitlementName.CNAME,
    );

    return isEntitlementEnabled(cnameEntitlement?.value);
  });

  const isEntitledDnsRoutingManualFailoverPolicy = computed(() => {
    const currentSubscription = subscriptionsStore.currentSubscription;
    if (!currentSubscription) {
      return false;
    }
    const manualFailoverEntitlement = entitlementsService.getEntitlement(
      currentSubscription,
      EntitlementName.MANUAL_FAILOVER_POLICY,
    );

    return isEntitlementEnabled(manualFailoverEntitlement?.value);
  });

  const isEntitledDnsRoutingGeoLocationPolicy = computed(() => {
    const currentSubscription = subscriptionsStore.currentSubscription;
    if (!currentSubscription) {
      return false;
    }
    const geoLocationEntitlement = entitlementsService.getEntitlement(
      currentSubscription,
      EntitlementName.GEOLOCATION_POLICY,
    );

    return isEntitlementEnabled(geoLocationEntitlement?.value);
  });

  const isEntitledLogsStreamer = computed(() => {
    const currentSubscription = subscriptionsStore.currentSubscription;
    if (!currentSubscription) {
      return false;
    }
    const entitlement = entitlementsService.getEntitlement(
      currentSubscription,
      EntitlementName.LOG_STREAMER,
    );

    return isEntitlementEnabled(entitlement?.value);
  });

  const isAiAddendumApplicable = computed(() => {
    const state = subscriptionsStore.currentSubscription?.aiAddendumState;
    return isAiAddendumStateApplicable(state);
  });

  const isSubscriptionActive = computed(
    () => subscriptionsStore.currentSubscription?.meta.isActive,
  );

  const isSubscriptionOnHold = computed(() => {
    return subscriptionsStore.currentSubscription?.state === "ONHOLD";
  });

  const hasSomethingToShowInEditSubscription = computed<boolean>(
    () =>
      requestDowngradeSubscriptionMeta.value ||
      removeSecurityPackMeta.value ||
      cancelSubscriptionMeta.value,
  );

  return {
    hasSomethingToShowInEditSubscription,
    requestDowngradeSubscriptionMeta,
    removeSecurityPackMeta,
    cancelSubscriptionMeta,
    artifactoryConfigurationTransferMeta,
    publicRootCertificateMeta,
    cloudChangesMeta,
    isEntitledCname,
    isEntitledLogsStreamer,
    isSubscriptionActive,
    isSubscriptionOnHold,
    isAiAddendumApplicable,
    isEntitledDnsRoutingGeoLocationPolicy,
    isEntitledDnsRoutingManualFailoverPolicy,
  };
};
